<template>
	<div class="sidebar-micro">
		<div class="sidebar-micro-buttons">
			<!-- User/Business Selector Button -->
			<div class="user-business-selector" @click="handleUserBusinessClick" v-if="!isUserSection">
				<div class="selector-button">
					<div v-if="currBusiness && currBusiness.logo" class="wrapper-img">
						<img :src="currBusiness.logo" alt="Business logo" class="business-logo">
					</div>
					<div class="wrapper-content" v-if="user && currBusiness">
						<p class="user-name">{{ currBusiness.name }}</p>
						<p class="user-label">{{ user.name }}</p>
					</div>
				</div>
			</div>
			
			<SidebarMicroButton
				v-for="(item, index) in items"
				:ref="'micro-button-'+index"
				:key="index"
				:icon-name="item.icon"
				:title="item.name"
				:locked="item.locked"
				:warning="item.warning"
				:error="item.error"
				:shown="item.hasOwnProperty('shown') ? item.shown : true"
				:active="index === currentItem"
				:active-child="currentChildItem"
				:children="item.children"
				:class="microButtonClass(item)"
				@button-click="(newState) => clickItem(index, newState)"
				@child-button-click="(childIndex, active) => clickChild(index, childIndex, active)"
			/>
		</div>
		
		<!-- Business Picker Bottom Sheet -->
		<AppBottomSheet :forceWidth="bottomSheetWidth" :active="businessPickerActive" @toggling-popup="togglingBusinessPicker">
			<BusinessPicker v-if="businessPickerActive" @done="businessPickerActive = false" />
		</AppBottomSheet>
	</div>
</template>

<script>
import SidebarMicroButton from "@/console-new/components/shared/SidebarMicroButton.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import BusinessPicker from "@/console-new/components/shared/BusinessPicker.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import {useUserStore} from "@/console-new/stores";
import {useLanguageStore} from "@/shared/stores";

export default {
	setup() {
		const userStore = useUserStore();
		const languageStore = useLanguageStore();
		return {
			userStore,
			getString: languageStore.getString
		}
	},
	emits: ["switched-item", "switched-child-item"],
	name: "SidebarMicro",
	components: {SidebarMicroButton, AppBottomSheet, BusinessPicker},
	mixins: [WindowWidth],
	props: {
		basePath: {
			type: String,
			required: true
		},
		items: {
			type: Array,
			required: true,
			validator(value) {
				return value.every(item => item.name && item.icon && (item.path || item.beforeEmitFunction) && (!item.children || item.children.length > 0));
			}
		},
		isMicro: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			currentItem: 0,
			currentChildItem: 0,
			businessPickerActive: false
		}
	},
	computed: {
		isUserSection() {
			return location && location.href && location.href.startsWith(URL_HTML + '/user');
		},
		bottomSheetWidth() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else {
				return "720px";
			}
		},
		currBusiness() {
			return this.userStore.currBusiness;
		},
		user() {
			return this.userStore.user;
		}
	},
	methods: {
		handleUserBusinessClick() {
			this.businessPickerActive = true;
		},
		togglingBusinessPicker(active) {
			if(!active) {
				this.businessPickerActive = false;
			}
		},
		microButtonClass(item) {
			let finalClass = '';
			if(item.topAuto) {
				finalClass += 'top-auto ';
			}
			if(item.bgEmphasis) {
				finalClass += 'bg-emphasis ';
			}
			if(item.hasOwnProperty('class')) {
				finalClass += item.class;
			}
			return finalClass;
		},
		clickItem(index, active = false) {
			if(this.items[index].locked) {
				if(this.items[index].lockedClick) {
					this.items[index].lockedClick();
				}
			} else {
				this.switchedItem(index, active);
			}
		},
		clickChild(index, childIndex, active) {
			if(this.items[index].children[childIndex].locked) {
				if(this.items[index].children[childIndex].lockedClick) {
					this.items[index].children[childIndex].lockedClick();
				}
			} else {
				this.switchedChild(index, childIndex, active)
			}
		},
		switchedItem(index, active) {
			let currButton = this.items[index];
			if(!currButton) { return }
			if(currButton.beforeEmitFunction) {
				currButton.beforeEmitFunction();
			}
			if(active && currButton.path) {
				this.currentItem = index;
				this.currentChildItem = 0;
				if(!currButton.children || !currButton.children.length) {
					this.$router.push(this.basePath + currButton.path);
				} else {
					this.$router.push(this.basePath + currButton.path + currButton.children[0].path);
				}
			} else {
				this.$emit("switched-item", this.currentItem);
			}
		},
		switchedChild(index, childIndex, active) {
			let currButton = this.items[index] && this.items[index].children && this.items[index].children[childIndex];
			if(!currButton) { return }
			if(currButton.beforeEmitFunction) {
				currButton.beforeEmitFunction();
			}
			if(active) {
				this.$router.push(this.basePath+this.items[index].path + currButton.path);
			} else {
				this.$emit("switched-child-item", this.currentChildItem);
			}
		},
		checkValidRoute() {
			let path = this.$route.path;
			let activeItem = this.items.find(i => i.shown);
			activeItem = activeItem ? activeItem : this.items[0];
			let activeChildItem = activeItem.children ? activeItem.children[0] : null;
			this.items.forEach(item => {
				if(path.includes(item.path)) {
					activeItem = item;
					if(!!item.children) {
						activeChildItem = item.children[0];
						item.children.forEach(child => {
							if(path.includes(item.path+child.path)) {
								activeChildItem = child;
							}
						});
					} else {
						activeChildItem = null;
					}
				}
			});

			let allowAllChildren = activeItem.hasOwnProperty("allowAllChildren") && activeItem.allowAllChildren;
			// If we're at the exact base path, redirect to first item even with allowAllChildren
			let isExactBasePath = path === this.basePath || path === this.basePath + '/';
			let finalPath = (allowAllChildren && !isExactBasePath) ? path : this.basePath + activeItem.path + (activeChildItem ? activeChildItem.path : "");
			if(path !== finalPath) {
				this.$router.push(finalPath);
			}
		},
	},
	watch: {
		"$route": {
			handler: function(newRoute) {
				if(!newRoute || !newRoute.path || !newRoute.path.startsWith(this.basePath)) {
					return
				}
				if(!this.isMicro) {
					this.checkValidRoute();
				}
				let self = this;
				this.items.forEach((item, index) => {
					if(self.$route.path.includes(item.path)) {
						self.currentItem = index;
					}
					if(!!item.children) {
						item.children.forEach((child, childIndex) => {
							if(self.$route.path.includes(item.path+child.path)) {
								self.currentChildItem = childIndex;
							}
						});
					}
				});
			},
			immediate: true
		},
		currentItem() {
			this.$emit("switched-item", this.currentItem);
		},
		currentChildItem() {
			this.$emit("switched-child-item", this.currentChildItem);
		}
	},
}
</script>

<style lang="scss" scoped>
.sidebar-micro {
	background-color: var(--color-bg);
	height: 100%;

	.sidebar-micro-buttons {
		display: flex;
		flex-direction: column;
		height: 100%;
		padding-bottom: 10px;
	}

	.top-auto {
		margin-top: auto;
	}

	.bg-emphasis  {
		padding: 0 10px;

		:deep(.sidebar-micro-button-container) {
			display: flex;
			position: relative;
			cursor: pointer;
			width: 100%;
			background-color: #6C33CB1A;
			border-radius: 13px;
			&:hover {
				background-color: rgba(108, 51, 203, 0.20);
			}
			&:active {
				background-color: rgba(108, 51, 203, 0.30);
			}
		}

	}

}
.sidebar-micro-buttons {
	.main-button {
		background-color: var(--color-bg-primary-1);
		&.with-border {
			border-bottom: 1px solid $alpha_4;
		}
	}
}

.user-business-selector {
	padding: 8px 14px;
	height: 60px;
	background-color: var(--color-bg-primary-2);
	display: flex;
	gap: 10px;
	
	.selector-button {
		width: 100%;
		height: 100%;
		border-radius: 13px;
		overflow: hidden;
		cursor: pointer;
		background-color: transparent;
		display: flex;
		align-items: center;
		justify-content: flex-start;
		transition: all 0.3s $transition;
		margin: 0 auto;
		gap: 10px;
		padding: 6px 8px;
		transform: translateX(-9px);

		.wrapper-img {
			width: 30px;
			height: 30px;
			border-radius: 8px;
			overflow: hidden;
			flex-shrink: 0;
			position: relative;
			.business-logo {
				width: 100%;
				height: 100%;
				object-fit: cover;
			}
		}
		&:hover {
			background-color: var(--color-bg-primary-1);
			transform: translateX(0);
		}
	}
	.wrapper-content {
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: flex-start;
		flex: 1;
		gap: 1px;
		.user-label {
			font-size: 10px;
			color: var(--color-txt-1);
			margin: 0;
			padding: 0;
			line-height: 1;
			white-space: nowrap;
			text-overflow: ellipsis;
			overflow: hidden;
			width: 100%;
			font-weight: 500;
		}

		.user-name {
			font-size: 14px;
			color: var(--color-txt);
			margin: 0;
			padding: 0;
			line-height: 1.2;
			white-space: nowrap;
			text-overflow: ellipsis;
			overflow: hidden;
			width: 100%;
			font-weight: 600;
		}
	}
}
</style>